import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Table,
  Badge,
  Modal,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { FaArrowLeft, FaExchangeAlt, FaSync, FaUtensils } from 'react-icons/fa';
import { procesosAlimentacionApi } from '../../../services/api/procesosAlimentacionApi';
import { comedorApi } from '../../../services/api/comedorApi';
import 'react-toastify/dist/ReactToastify.css';

const TrasladoComensalesComedor = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');

  const [comedores, setComedores] = useState([]);
  const [origenId, setOrigenId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [loadingComedores, setLoadingComedores] = useState(false);
  const [loadingCandidatos, setLoadingCandidatos] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [candidatos, setCandidatos] = useState([]);
  const [resumenCandidatos, setResumenCandidatos] = useState(null);
  const [ultimoResultado, setUltimoResultado] = useState(null);
  const [statsComedorComensal, setStatsComedorComensal] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!token || !currentUser) {
      navigate('/');
    }
  }, [navigate, token, currentUser]);

  const cargarComedores = useCallback(async () => {
    if (!selectedProject?.id) {
      toast.error('Seleccione un proyecto en la barra superior.');
      return;
    }
    setLoadingComedores(true);
    try {
      const res = await comedorApi.getByProyecto(selectedProject.id);
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : raw?.data || [];
      // Incluir comedores inactivos: a veces el histórico de asignaciones está en un comedor dado de baja.
      setComedores(list);
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar los comedores del proyecto.');
      setComedores([]);
    } finally {
      setLoadingComedores(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    cargarComedores();
  }, [cargarComedores]);

  const consultarCandidatos = async () => {
    if (!origenId || !destinoId) {
      toast.warning('Seleccione comedor origen y comedor destino.');
      return;
    }
    if (String(origenId) === String(destinoId)) {
      toast.warning('Origen y destino deben ser distintos.');
      return;
    }
    setLoadingCandidatos(true);
    setCandidatos([]);
    setResumenCandidatos(null);
    setUltimoResultado(null);
    setStatsComedorComensal(null);
    try {
      const res = await procesosAlimentacionApi.getCandidatosTrasladoComensalesComedor(
        parseInt(origenId, 10),
        parseInt(destinoId, 10)
      );
      const d = res.data;
      if (d.success) {
        setResumenCandidatos({
          origen: d.comedor_origen,
          destino: d.comedor_destino,
          total: d.total_candidatos,
        });
        setCandidatos(d.candidatos || []);
        setStatsComedorComensal(d.estadisticas_comedor_comensal || null);
        if (d.total_candidatos === 0) {
          toast.info('No hay comensales a trasladar (todos ya están en el destino).');
        } else {
          toast.success(`${d.total_candidatos} comensal(es) listo(s) para trasladar.`);
        }
      }
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Error al consultar candidatos.';
      toast.error(typeof msg === 'string' ? msg : 'Error al consultar.');
    } finally {
      setLoadingCandidatos(false);
    }
  };

  const abrirConfirmacionTraslado = () => {
    if (!origenId || !destinoId) {
      toast.warning('Seleccione comedor origen y comedor destino.');
      return;
    }
    if (String(origenId) === String(destinoId)) {
      toast.warning('Origen y destino deben ser distintos.');
      return;
    }
    if (!resumenCandidatos || resumenCandidatos.total === 0) {
      toast.info('Primero consulte candidatos; no hay nada que ejecutar.');
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmarEjecutarTraslado = async () => {
    setShowConfirmModal(false);
    setEjecutando(true);
    setUltimoResultado(null);
    try {
      const res = await procesosAlimentacionApi.ejecutarTrasladoComensalesComedor(
        parseInt(origenId, 10),
        parseInt(destinoId, 10)
      );
      const d = res.data;
      if (d.success) {
        setUltimoResultado(d);
        toast.success(d.message || 'Traslado completado.');
        setCandidatos([]);
        setResumenCandidatos(null);
        setStatsComedorComensal(null);
      }
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Error al ejecutar el traslado.';
      toast.error(typeof msg === 'string' ? msg : 'Error al ejecutar.');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <>
      <style>
        {`
          .dh-proc-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-proc-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 1.75rem 0;
            margin-bottom: 1.5rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-proc-title { font-size: 1.65rem; font-weight: 700; margin: 0; }
          .dh-proc-sub { font-size: 0.95rem; opacity: 0.9; margin: 0.35rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
          }
          .dh-card-h {
            border-radius: 20px 20px 0 0;
            font-weight: 700;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: #fff;
            border: none;
            padding: 1.25rem 1.75rem;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
          }
          .btn-dh-primary:hover:not(:disabled) { color: white; filter: brightness(1.06); }
          .btn-dh-primary:disabled { opacity: 0.65; }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 20px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; filter: brightness(1.05); }
          .btn-dh-header-light {
            background: rgba(255, 255, 255, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.45);
            color: #fff;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-header-light:hover { color: #fff; background: rgba(255, 255, 255, 0.3); }
          .dh-modal-header .modal-title { font-weight: 700; }
          .dh-modal-header .btn-close { filter: invert(1); opacity: 0.9; }
          .dh-accent { color: #667eea; font-weight: 700; }
        `}
      </style>
      <div className="dh-proc-bg">
        <ToastContainer position="top-right" />
        <div className="dh-proc-header">
          <Container>
            <Row className="align-items-center">
              <Col>
                <h1 className="dh-proc-title">
                  <FaExchangeAlt className="me-2" />
                  Traslado de comensales entre comedores
                </h1>
                <p className="dh-proc-sub">
                  Tabla <strong>comedor_comensal</strong>: pasa al destino solo los comensales que
                  están en el origen y que el destino <strong>aún no tiene</strong>; luego se
                  quitan del origen.
                </p>
              </Col>
              <Col xs="auto">
                <Button className="btn-dh-header-light" onClick={() => navigate(-1)}>
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="pb-5">
          {!selectedProject?.id && (
            <Alert variant="warning">Seleccione un proyecto para listar comedores.</Alert>
          )}

          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-h">
              <FaUtensils className="me-2" />
              Comedores del proyecto
            </Card.Header>
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={4}>
                  <Form.Label>Comedor origen</Form.Label>
                  <Form.Select
                    value={origenId}
                    onChange={(e) => setOrigenId(e.target.value)}
                    disabled={loadingComedores}
                  >
                    <option value="">— Seleccione —</option>
                    {comedores.map((c) => (
                      <option key={`o-${c.id}`} value={c.id}>
                        {c.nombre} (id {c.id}
                        {c.activo === false || c.activo === 0 ? ' — inactivo' : ''})
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label>Comedor destino</Form.Label>
                  <Form.Select
                    value={destinoId}
                    onChange={(e) => setDestinoId(e.target.value)}
                    disabled={loadingComedores}
                  >
                    <option value="">— Seleccione —</option>
                    {comedores.map((c) => (
                      <option key={`d-${c.id}`} value={c.id}>
                        {c.nombre} (id {c.id}
                        {c.activo === false || c.activo === 0 ? ' — inactivo' : ''})
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4} className="d-flex flex-wrap gap-2">
                  <Button variant="outline-secondary" onClick={cargarComedores} disabled={loadingComedores}>
                    {loadingComedores ? <Spinner size="sm" animation="border" /> : <FaSync className="me-1" />}
                    Recargar comedores
                  </Button>
                  <Button
                    className="btn-dh-primary"
                    onClick={consultarCandidatos}
                    disabled={loadingCandidatos}
                  >
                    {loadingCandidatos ? (
                      <Spinner size="sm" animation="border" className="me-1" />
                    ) : null}
                    Consultar candidatos
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {statsComedorComensal && (
            <Alert variant="secondary" className="small py-2 mb-3">
              <strong>Diagnóstico comedor_comensal:</strong>{' '}
              filas en origen: <strong>{statsComedorComensal.filas_en_origen}</strong>
              {' · '}comensales distintos en origen:{' '}
              <strong>{statsComedorComensal.comensales_distintos_en_origen}</strong>
              {' · '}filas en destino: <strong>{statsComedorComensal.filas_en_destino}</strong>
              {' · '}distintos ya en ambos:{' '}
              <strong>{statsComedorComensal.comensales_distintos_ya_en_destino}</strong>
              {' · '}distintos sin maestro en <code>comensales</code>:{' '}
              <strong>{statsComedorComensal.distintos_sin_fila_comensales ?? 0}</strong>
              <span className="text-muted d-block mt-1">
                Si «candidatos» coincide con «distintos en origen» pero esperaba más filas, revise
                comedor duplicado/inactivo en el desplegable. Las filas huérfanas (sin maestro) no se
                pueden insertar en destino por FK hasta corregirlas.
              </span>
            </Alert>
          )}

          {resumenCandidatos && (
            <Alert variant="info" className="d-flex align-items-center flex-wrap gap-2">
              <span>
                <strong>{resumenCandidatos.origen?.nombre}</strong>
                {' → '}
                <strong>{resumenCandidatos.destino?.nombre}</strong>
              </span>
              <Badge bg="dark">Candidatos: {resumenCandidatos.total}</Badge>
              <Button
                className="btn-dh-primary"
                size="sm"
                onClick={abrirConfirmacionTraslado}
                disabled={ejecutando || resumenCandidatos.total === 0}
              >
                {ejecutando ? <Spinner size="sm" animation="border" /> : 'Ejecutar traslado'}
              </Button>
            </Alert>
          )}

          {ultimoResultado && (
            <Alert variant="success">
              {ultimoResultado.message}
              <div className="small mt-1">
                Trasladados: <strong>{ultimoResultado.trasladados}</strong>
                {ultimoResultado.eliminados_origen != null && (
                  <>
                    {' '}
                    · Filas eliminadas en origen: <strong>{ultimoResultado.eliminados_origen}</strong>
                  </>
                )}
              </div>
            </Alert>
          )}

          <Card className="dh-card">
            <Card.Header className="dh-card-h">Vista previa (candidatos)</Card.Header>
            <Card.Body className="p-0">
              {loadingCandidatos && (
                <div className="p-4 text-center text-muted">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando…
                </div>
              )}
              {!loadingCandidatos && candidatos.length === 0 && (
                <p className="p-4 mb-0 text-muted small">
                  Elija origen y destino, luego «Consultar candidatos» para ver la grilla.
                </p>
              )}
              {candidatos.length > 0 && (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0 small">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Nombre</th>
                        <th>DNI</th>
                        <th>Doc. comedor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidatos.map((c) => (
                        <tr key={c.comensal_id}>
                          <td>{c.comensal_id}</td>
                          <td>{c.nombre_comensal}</td>
                          <td>{c.numero_documento_identidad || '—'}</td>
                          <td>{c.numero_documento_comedor || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>

        <Modal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          centered
          backdrop="static"
          contentClassName="border-0 shadow rounded-3 overflow-hidden"
        >
          <Modal.Header
            closeButton
            className="dh-modal-header text-white border-0"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px 12px 0 0',
            }}
          >
            <Modal.Title>Confirmar traslado</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-4 pb-2">
            {resumenCandidatos && (
              <>
                <p className="mb-2">
                  Se trasladarán{' '}
                  <strong className="dh-accent">{resumenCandidatos.total}</strong> comensal(es).
                </p>
                <p className="mb-2 small text-muted">
                  <strong className="text-dark">{resumenCandidatos.origen?.nombre}</strong>
                  {' → '}
                  <strong className="text-dark">{resumenCandidatos.destino?.nombre}</strong>
                </p>
                <p className="mb-0 small">
                  Se crearán filas en <code>comedor_comensal</code> en el comedor destino y se
                  eliminarán las asignaciones del comedor origen para esos comensales.
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0 pb-3 gap-2">
            <Button className="btn-dh-secondary" onClick={() => setShowConfirmModal(false)}>
              Cancelar
            </Button>
            <Button className="btn-dh-primary" onClick={confirmarEjecutarTraslado}>
              Confirmar traslado
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default TrasladoComensalesComedor;
